// The map text of the chart furniture, as sprites (R548).
//
// This is the thin thing between the two halves a piece needs: the atlas,
// which bakes one cell a glyph, and `render/text-run.ts`, which says where
// each glyph goes. A piece holds one `ChartGlyphs` and writes a label with
// one call.
//
// It imports the atlas KEY helpers and the layer flags, and nothing else of
// the engine: a test hands it a `RecordingBatches` and reads back the keys
// and the places.
//
// It states no font family: `chartGlyphKey` names the face, and the bake
// sets each face in the family that `data/presentation/chart.toml`
// `[chart.text]` owns.
//
// A glyph draws from the cell `glyphCellKey` names for its ink, with the
// tint `glyphTint` gives (R3296, R3299). In the dark identity that is the
// tinted mask over the water halo, as before. In the light identity it is
// the cell baked in the ink over the paper halo, drawn with no tint, so a
// sounding, a name and an isobath figure stand on paper.

import {
  CHART_FALLBACK_ADVANCE_EM, CHART_MONO_ADVANCE_EM, chartGlyphKey, glyphCellKey, glyphTint,
  type ChartFace,
} from '../gfx/atlas';
import { CHART } from '../../gen/presentation';
import { F_SCREEN } from '../layer';
import type { BatchSource, QuadSink, SpriteEntry } from '../layers/batches';
import {
  layoutAlongPathInto, layoutRunInto, runScratch, runWidth,
  type AlongOpts as LayoutAlongOpts, type Anchor, type GlyphMetrics, type Pt,
} from '../../render/text-run';
import { stepFlags, stepPx, type ChartStep } from './steps';

/** What a straight run takes. */
export interface RunOpts {
  anchor?: Anchor;
  letterSpacing?: number;
  /**
   * Hold the run at one screen size, whatever the zoom.
   *
   * The quad carries `F_SCREEN`, so the shader undoes the zoom about the
   * pivot. The offsets of the layout are therefore divided by `zoom` here,
   * exactly as the damage figure does it.
   */
  screen?: boolean;
  zoom?: number;
  /** A scale on every cell. 1 draws a cell at the size it was baked at. */
  scale?: number;
  /**
   * False writes the run in the BARE cells, which bake no halo under the
   * ink. Every chart piece keeps the default; the legend of the hold-fire
   * look `restricted-line` passes false, as its card does (R1439).
   */
  halo?: boolean;
}

/** What a run along a path takes: the layout's options, plus a scale on every cell. */
export interface AlongOpts extends LayoutAlongOpts {
  scale?: number;
}

/**
 * What a word at a chart text step takes (R1796). A word is world-placed and
 * held in the screen band, so it takes no `screen` and no `zoom`.
 */
export interface WordOpts {
  anchor?: Anchor;
  letterSpacing?: number;
  scale?: number;
  halo?: boolean;
  /**
   * The point the band scale grows the word about. A straight word takes
   * its own `x, y` by default, a word along a path its middle glyph. Two
   * lines of one block pass one pivot, so they keep their gap on screen.
   */
  pivot?: Pt;
}

/** What a word along a path takes: the layout's options and the word's own. */
export interface WordAlongOpts extends LayoutAlongOpts {
  scale?: number;
  pivot?: Pt;
}

/** The sizes a face is baked at. A piece's size snaps to the nearest. */
export interface BakedSizes { mono: readonly number[]; fell: readonly number[] }

/** The cell a glyph draws as when the atlas can hold no more: nothing. */
const BLANK: SpriteEntry = { key: 'chart:blank', u0: 0, v0: 0, u1: 0, v1: 0, w: 0, h: 0, ox: 0, oy: 0 };

export class ChartGlyphs {
  /** Set once the atlas refused a cell; every later glyph is a blank. */
  private full = false;
  /** The places of the run in hand. One scratch, so a run allocates no place. */
  private readonly laid = runScratch();
  /**
   * The advances of the run in hand. One object with one bound reader,
   * rewritten by `runMetrics` for each run, so a word allocates no metrics
   * and no closure (§10.3). A run reads it to the end before the next run
   * rewrites it.
   */
  private readonly runM: GlyphMetrics = { advance: (ch: string) => this.runAdvance(ch) };
  private runFace: ChartFace = 'mono';
  private runSize = 0;
  private runBaked = 0;
  private runHalo = true;
  /** The ink of the run in hand, so an advance reads the cell the run draws. */
  private runInk: number | undefined = undefined;

  constructor(
    private readonly src: BatchSource,
    readonly sizes: BakedSizes = { mono: CHART.text.monoSizesPx, fell: CHART.text.fellSizesPx },
  ) {}

  /**
   * The size a face is baked at for a size a piece asked for: the nearest
   * of the baked list, the larger on a tie.
   *
   * Every distinct size is one whole alphabet of cells, so a piece's own
   * figure (6.5, 7.5, 9.5 ...) must not become one. The quad is scaled by
   * `size / baked` instead, which at these distances is under a texel.
   */
  baked(face: ChartFace, size: number): number {
    const list = this.sizes[face];
    if (list.length === 0) return size;
    let best = list[0];
    for (const s of list) {
      const d = Math.abs(s - size), bd = Math.abs(best - size);
      if (d < bd || (d === bd && s > best)) best = s;
    }
    return best;
  }

  /**
   * The atlas entry of one glyph at a baked size, baked the first time it
   * is asked for.
   *
   * A full atlas is not a reason to take a piece off the map: the glyph
   * draws as a blank, the console says so one time, and the rest of the
   * furniture keeps drawing.
   *
   * `ink` names the cell of the glyph in that ink (`glyphCellKey`, R3296):
   * the mask in the dark identity, the inked cell in the light one. With
   * no ink, the entry is the mask, which serves an advance.
   */
  entry(face: ChartFace, baked: number, ch: string, halo = true, ink?: number): SpriteEntry {
    if (this.full) return BLANK;
    try {
      const key = chartGlyphKey(face, baked, ch, halo);
      return this.src.sprite(ink === undefined ? key : glyphCellKey(key, ink));
    } catch (err) {
      if (!/atlas is full/.test(String(err))) throw err;
      this.full = true;
      console.warn('chart: the glyph atlas is full; further glyphs draw as blanks', err);
      return BLANK;
    }
  }

  /**
   * Any atlas cell by key, for a piece that draws a MARK and not a word.
   *
   * The battle marks of card 52 cross two of the fleet layer's own dart
   * cells over the water they were fought in, and a piece holds no path to
   * the batch source of its own. It is here, beside `entry`, because the
   * atlas is what both of them reach.
   */
  mark(key: string): SpriteEntry {
    return this.src.sprite(key);
  }

  /**
   * The advances of one face at one size.
   *
   * The mono face states one advance for every glyph, so that answer is
   * arithmetic and asks the atlas for nothing. The Fell face is
   * proportional, so the answer is what the bake measured — and the
   * fallback em where the cell carries no advance, which is jsdom and a
   * recorder.
   */
  metrics(face: ChartFace, size: number, halo = true): GlyphMetrics {
    if (face === 'mono') {
      const adv = size * CHART_MONO_ADVANCE_EM;
      return { advance: () => adv };
    }
    const baked = this.baked(face, size);
    return { advance: (ch: string) => this.fellAdvance(face, size, baked, ch, halo) };
  }

  /** One advance of a proportional face: what the bake measured, scaled
   *  from the baked size, or the fallback em where the cell carries none. */
  private fellAdvance(
    face: ChartFace, size: number, baked: number, ch: string, halo: boolean, ink?: number,
  ): number {
    const adv = this.entry(face, baked, ch, halo, ink).adv;
    return adv === undefined ? size * CHART_FALLBACK_ADVANCE_EM : adv * (size / baked);
  }

  /**
   * The advances of one face at one size for the run in hand: the answer
   * of `metrics`, in the one object of the instance (§10.3). The ink of
   * the run names the cell a Fell advance reads, so the light identity
   * bakes no mask cell beside the inked cell the run draws (R3299).
   */
  private runMetrics(face: ChartFace, size: number, halo: boolean, ink: number): GlyphMetrics {
    this.runFace = face;
    this.runSize = size;
    this.runBaked = face === 'mono' ? size : this.baked(face, size);
    this.runHalo = halo;
    this.runInk = ink;
    return this.runM;
  }

  /** One advance of the run in hand, as `metrics` answers it. */
  private runAdvance(ch: string): number {
    if (this.runFace === 'mono') return this.runSize * CHART_MONO_ADVANCE_EM;
    return this.fellAdvance(this.runFace, this.runSize, this.runBaked, ch, this.runHalo, this.runInk);
  }

  /** The advances of one face at one step. */
  stepMetrics(face: ChartFace, step: ChartStep, halo = true): GlyphMetrics {
    return this.metrics(face, stepPx(face, step), halo);
  }

  /** One glyph of a word: its place, the pivot of the word and the step flags. */
  private putGlyph(
    quads: QuadSink, px: number, py: number, x: number, y: number, rot: number,
    face: ChartFace, size: number, ch: string,
    tint: number, alpha: number, scale: number, flags: number, halo = true,
  ): void {
    const baked = this.baked(face, size);
    const e = this.entry(face, baked, ch, halo, tint);
    if (e === BLANK) return;
    quads.glyph(px, py, x, y, rot, e, glyphTint(tint), alpha, scale * (size / baked), flags);
  }

  /**
   * A straight word at a chart text step, and the width it took at a band
   * scale of 1 (R1796, §17.4).
   *
   * The face sets the size of the step. Every glyph carries the step in its
   * flags and keeps its offset from one pivot, so the quad shader holds the
   * word inside the screen band and hides it under the step's zoom gate,
   * with no repack. A blank word and a word at no alpha write nothing.
   */
  word(
    quads: QuadSink, text: string, x: number, y: number,
    face: ChartFace, step: ChartStep, tint: number, alpha: number,
    opts: WordOpts = {},
  ): number {
    const size = stepPx(face, step);
    const halo = opts.halo ?? true;
    const m = this.runMetrics(face, size, halo, tint);
    if (text === '' || alpha <= 0) return runWidth(text, m, opts.letterSpacing ?? 0);
    const flags = stepFlags(step);
    const scale = opts.scale ?? 1;
    const px = opts.pivot?.x ?? x, py = opts.pivot?.y ?? y;
    const laid = this.laid;
    layoutRunInto(text, m, opts, laid);
    for (let i = 0; i < laid.n; i++) {
      const g = laid.glyphs[i];
      this.putGlyph(quads, px, py, x + g.x, y + g.y, 0, face, size, g.ch, tint, alpha, scale, flags, halo);
    }
    return laid.width;
  }

  /**
   * A word at a chart text step laid along a polyline, in WORLD px (R1796).
   *
   * The band scale grows the word about its middle glyph, so the word keeps
   * its curve and its place over the body it names, and its screen size
   * stays inside the band.
   */
  wordAlong(
    quads: QuadSink, text: string, pts: readonly Pt[],
    face: ChartFace, step: ChartStep, tint: number, alpha: number,
    opts: WordAlongOpts = {},
  ): void {
    if (text === '' || alpha <= 0 || pts.length === 0) return;
    const size = stepPx(face, step);
    const m = this.runMetrics(face, size, true, tint);
    const flags = stepFlags(step);
    const scale = opts.scale ?? 1;
    const laid = this.laid;
    layoutAlongPathInto(text, m, pts, opts, laid);
    if (laid.n === 0) return;
    let px: number, py: number;
    if (opts.pivot) {
      px = opts.pivot.x; py = opts.pivot.y;
    } else {
      const a = laid.glyphs[(laid.n - 1) >> 1], b = laid.glyphs[laid.n >> 1];
      px = (a.x + b.x) / 2; py = (a.y + b.y) / 2;
    }
    for (let i = 0; i < laid.n; i++) {
      const g = laid.glyphs[i];
      this.putGlyph(quads, px, py, g.x, g.y, g.rot, face, size, g.ch, tint, alpha, scale, flags);
    }
  }

  /** One glyph, at a place and an angle. */
  put(
    quads: QuadSink, x: number, y: number, rot: number,
    face: ChartFace, size: number, ch: string,
    tint: number, alpha: number, scale: number, screen: boolean, halo = true,
  ): void {
    const baked = this.baked(face, size);
    const e = this.entry(face, baked, ch, halo, tint);
    if (e === BLANK) return;
    quads.sprite(x, y, rot, e, glyphTint(tint), alpha, scale * (size / baked), screen ? F_SCREEN : 0);
  }

  /**
   * A straight run at one place, and the width it took.
   *
   * A blank run and a run at no alpha write nothing, so a piece can call
   * this without a guard of its own.
   */
  run(
    quads: QuadSink, text: string, x: number, y: number,
    face: ChartFace, size: number, tint: number, alpha: number,
    opts: RunOpts = {},
  ): number {
    const halo = opts.halo ?? true;
    const m = this.runMetrics(face, size, halo, tint);
    if (text === '' || alpha <= 0) return runWidth(text, m, opts.letterSpacing ?? 0);
    const screen = opts.screen ?? false;
    const k = screen ? 1 / (opts.zoom ?? 1) : 1;
    const scale = opts.scale ?? 1;
    const laid = this.laid;
    layoutRunInto(text, m, opts, laid);
    for (let i = 0; i < laid.n; i++) {
      const g = laid.glyphs[i];
      this.put(quads, x + g.x * k, y + g.y * k, 0, face, size, g.ch, tint, alpha, scale, screen, halo);
    }
    return laid.width;
  }

  /**
   * A run laid along a polyline, in WORLD px.
   *
   * It divides nothing by the zoom and sets no screen flag: a name along a
   * rim belongs to the water and grows with it.
   */
  along(
    quads: QuadSink, text: string, pts: readonly Pt[],
    face: ChartFace, size: number, tint: number, alpha: number,
    opts: AlongOpts = {},
  ): void {
    if (text === '' || alpha <= 0 || pts.length === 0) return;
    const m = this.runMetrics(face, size, true, tint);
    const scale = opts.scale ?? 1;
    const laid = this.laid;
    layoutAlongPathInto(text, m, pts, opts, laid);
    for (let i = 0; i < laid.n; i++) {
      const g = laid.glyphs[i];
      this.put(quads, g.x, g.y, g.rot, face, size, g.ch, tint, alpha, scale, false);
    }
  }
}
